import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import {
  useGetAllAssessments,
  useGetAssessmentResults,
  useGetReportDownloadUrl,
} from '@features/assessment';
import { useDirection } from '@hooks';
import { AssessmentStatus, LimitPerPage, Order } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import {
  ArrowRightIcon,
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  DownloadSimpleIcon,
  PlusIcon,
  ShieldCheckIcon,
} from '@phosphor-icons/react';

import { CircleMeter } from './CircleMeter';
import { DashboardWidget } from './DashboardWidget';
import { isCriticalGap, scoreBand } from './score-band';

/** Locale-aware short date, tolerant of null/invalid input. */
const useShortDate = () => {
  const { i18n } = useTrans();
  return (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(d);
  };
};

const DomainBar: FC<{ name: string; value: number }> = observer(({ name, value }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const { color } = scoreBand(value, theme);
  const critical = isCriticalGap(value);
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.625 }}>
        <Typography
          sx={{ flex: 1, fontSize: 12.5, fontWeight: critical ? 600 : 400, color: 'text.primary' }}
          noWrap
        >
          {name}
        </Typography>
        {critical && (
          <Chip
            label={t('app.dashboard.maturity.criticalGap')}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 600,
              color: theme.palette.red[500],
              backgroundColor: theme.palette.red[100],
              '& .MuiChip-label': { px: 0.875 },
            }}
          />
        )}
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color, minWidth: 34, textAlign: 'end' }}>
          {value}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, value))}
        sx={{
          height: 6,
          borderRadius: 999,
          backgroundColor: theme.palette.grey[300],
          '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 999 },
        }}
      />
    </Box>
  );
});

/**
 * Maturity hero: radial score + per-domain breakdown for the latest completed
 * assessment, a picker to switch between completed runs, and a report export.
 * Falls back to an empty state when the tenant has no completed assessments.
 */
export const MaturityHero: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();
  const navigate = useNavigate();
  const { isRtl } = useDirection();
  const formatDate = useShortDate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: completed, isLoading: listLoading } = useGetAllAssessments({
    status: AssessmentStatus.COMPLETED,
    sortBy: 'updatedAt',
    order: Order.DESC,
    page: 1,
    limit: LimitPerPage.TWENTY,
  });

  const runs = useMemo(() => completed?.rows ?? [], [completed]);
  const hasRuns = runs.length > 0;

  // Default the picker to the most-recent completed run once the list loads.
  useEffect(() => {
    if (!selectedId && runs.length) setSelectedId(runs[0].id);
  }, [runs, selectedId]);

  const selectedRun = runs.find((r) => r.id === selectedId) ?? runs[0] ?? null;

  const { data: results, isLoading: resultsLoading } = useGetAssessmentResults(
    { assessmentId: selectedRun?.id ?? '' },
    { enabled: !!selectedRun?.id },
  );

  const reportDownload = useGetReportDownloadUrl();

  const meta = results?.results ?? null;
  const score = meta?.overallResults?.maturity?.percentage ?? selectedRun?.score ?? 0;
  const domains = meta?.domains ?? [];
  const DOMAIN_PREVIEW = 5;
  const visibleDomains = domains.slice(0, DOMAIN_PREVIEW);
  const hiddenDomainCount = domains.length - DOMAIN_PREVIEW;

  const icon = <ShieldCheckIcon size={19} weight="regular" />;

  if (!listLoading && !hasRuns) {
    return (
      <DashboardWidget
        icon={icon}
        iconColor={theme.palette.blue[700]}
        title={t('app.dashboard.maturity.title')}
        subtitle={t('app.dashboard.maturity.subtitle')}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            p: 4,
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 17, fontWeight: 600 }}>
            {t('app.dashboard.maturity.empty.title')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', maxWidth: 340, mb: 1.5 }}>
            {t('app.dashboard.maturity.empty.body')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<PlusIcon size={15} />}
            onClick={() => navigate(routes.assessmentNew())}
            sx={{ textTransform: 'none' }}
          >
            {t('app.dashboard.maturity.empty.run')}
          </Button>
        </Box>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget
      icon={icon}
      iconColor={theme.palette.blue[700]}
      title={t('app.dashboard.maturity.title')}
      subtitle={
        selectedRun
          ? t('app.dashboard.maturity.latest', { date: formatDate(selectedRun.completedAt ?? selectedRun.updatedAt) })
          : t('app.dashboard.maturity.subtitle')
      }
      right={
        hasRuns ? (
          <>
            <Button
              variant="secondary"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              endIcon={anchorEl ? <CaretUpIcon size={13} /> : <CaretDownIcon size={13} />}
              sx={{ textTransform: 'none', maxWidth: 240, color: 'text.primary' }}
            >
              <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 500 }}>
                {selectedRun?.title}
              </Typography>
            </Button>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
              {runs.map((r) => (
                <MenuItem
                  key={r.id}
                  selected={r.id === selectedRun?.id}
                  onClick={() => {
                    setSelectedId(r.id);
                    setAnchorEl(null);
                  }}
                  sx={{ gap: 1.25, py: 1.25 }}
                >
                  <CheckCircleIcon size={18} color={theme.palette.green[700]} weight="fill" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 500 }} noWrap>
                      {r.title}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.tertiary' }}>
                      {formatDate(r.completedAt ?? r.updatedAt)}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 600, color: scoreBand(r.score ?? 0, theme).color }}
                  >
                    {r.score ?? 0}%
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : undefined
      }
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          gap: 3.5,
          p: 3,
          minHeight: 0,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            flexShrink: 0,
            width: 200,
          }}
        >
          <CircleMeter value={score} />
          <Button
            variant="contained"
            fullWidth
            startIcon={<DownloadSimpleIcon size={15} />}
            disabled={!selectedRun || reportDownload.isPending}
            onClick={() => selectedRun && reportDownload.mutate({ assessmentId: selectedRun.id })}
            sx={{ textTransform: 'none' }}
          >
            {t('app.dashboard.maturity.export')}
          </Button>
        </Box>

        <Box sx={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
              {t('app.dashboard.maturity.domainBreakdown')}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.tertiary' }}>
              {t('app.dashboard.maturity.domainsCount', { count: domains.length })}
            </Typography>
          </Box>
          {resultsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.375 }}>
              {visibleDomains.map((d) => (
                <DomainBar
                  key={d.frameworkDomain.id}
                  name={d.frameworkDomain.name}
                  value={d.results.maturity.percentage}
                />
              ))}
              {hiddenDomainCount > 0 && selectedRun && (
                <Button
                  variant="text"
                  onClick={() => navigate(routes.assessmentOverview(selectedRun.id))}
                  endIcon={<ArrowRightIcon size={13} style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: 12, mt: 0.5 }}
                >
                  {t('app.dashboard.maturity.viewAllDomains')}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </DashboardWidget>
  );
});
